import { useEffect, useRef } from 'react'

type WakeLockSentinelLike = {
  released: boolean
  release: () => Promise<void>
  addEventListener: (type: 'release', listener: () => void) => void
  removeEventListener: (type: 'release', listener: () => void) => void
}

type WakeLockAPI = {
  request: (type: 'screen') => Promise<WakeLockSentinelLike>
}

function getWakeLock(): WakeLockAPI | null {
  if (typeof navigator === 'undefined') return null
  const wl = (navigator as Navigator & { wakeLock?: WakeLockAPI }).wakeLock
  return wl ?? null
}

/**
 * Holds a screen wake lock while `active` is true. Automatically reacquires
 * after the tab becomes visible again (the browser releases the lock on hide).
 * No-op when the Wake Lock API is unavailable.
 */
export function useWakeLock(active: boolean): void {
  const sentinelRef = useRef<WakeLockSentinelLike | null>(null)
  const activeRef = useRef(active)
  activeRef.current = active

  useEffect(() => {
    const api = getWakeLock()
    if (!api) return

    let cancelled = false

    const release = async () => {
      const sentinel = sentinelRef.current
      sentinelRef.current = null
      if (sentinel && !sentinel.released) {
        try {
          await sentinel.release()
        } catch (err) {
          console.warn('[wakeLock] release failed', err)
        }
      }
    }

    const acquire = async () => {
      if (cancelled || !activeRef.current) return
      if (sentinelRef.current && !sentinelRef.current.released) return
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return
      try {
        const sentinel = await api.request('screen')
        if (cancelled || !activeRef.current) {
          await sentinel.release().catch(() => undefined)
          return
        }
        sentinelRef.current = sentinel
        const onReleased = () => {
          if (sentinelRef.current === sentinel) {
            sentinelRef.current = null
          }
          sentinel.removeEventListener('release', onReleased)
        }
        sentinel.addEventListener('release', onReleased)
      } catch (err) {
        console.warn('[wakeLock] request failed', err)
      }
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && activeRef.current) {
        void acquire()
      }
    }

    if (active) {
      void acquire()
      document.addEventListener('visibilitychange', handleVisibilityChange)
    }

    return () => {
      cancelled = true
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      void release()
    }
  }, [active])
}
